import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { FeatureToggles } from '@mwe/models';
import { computed, DestroyRef, inject } from '@angular/core';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { FeatureToggleEnum } from '@mwe/constants';
import { EnvironmentService } from '../environment/environment.service';
import { timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StaticContentService } from '../static-content/static-content.service';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const initialState = {
  _featureToggles: null as FeatureToggles,
  togglesLoaded: false as boolean,
};

export const FeatureTogglesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    _environmentService: inject(EnvironmentService),
    _staticContentService: inject(StaticContentService),
    _destroyRef: inject(DestroyRef),
  })),
  withComputed(store => {
    const enabledFeatureToggles = computed(() => {
      if (!store._featureToggles()) return [];

      const now = dayjs();
      return Object.entries(store._featureToggles() ?? {})
        .filter(([_, toggle]) => {
          // Check if start and end is set. If set check if we are currently with in the range.
          const hasStart = toggle?.start && toggle.start.trim() !== '';
          const hasEnd = toggle?.end && toggle.end.trim() !== '';

          const isWithinRange =
            (hasStart || hasEnd) &&
            (!hasStart || now.isSameOrAfter(dayjs(toggle.start))) &&
            (!hasEnd || now.isSameOrBefore(dayjs(toggle.end)));

          return isWithinRange ? !toggle.defaultValue : toggle.defaultValue;
        })
        .map(([key]) => key);
    });
    return {
      enabledFeatureToggles,
    };
  }),
  withMethods(store => {
    const isFeatureToggleEnabled = (featureToggle: FeatureToggleEnum) => {
      return store.enabledFeatureToggles().includes(featureToggle);
    };

    const isFeatureToggleDisabled = (featureToggle: FeatureToggleEnum) => {
      return !isFeatureToggleEnabled(featureToggle);
    };

    const isAnyFeatureToggleEnabled = (featureToggle: FeatureToggleEnum[]) => {
      return featureToggle.some(toggle => isFeatureToggleEnabled(toggle));
    };

    const areAllFeatureTogglesEnabled = (featureToggle: FeatureToggleEnum[]) => {
      return featureToggle.every(toggle => isFeatureToggleEnabled(toggle));
    };

    const areAllFeatureTogglesDisabled = (featureToggle: FeatureToggleEnum[]) => {
      return featureToggle.every(toggle => isFeatureToggleDisabled(toggle));
    };

    const _loadFeatureToggle = () => {
      const intervalMs = (store._environmentService?.getMaintenanceRefreshIntervall() ?? 5) * 60 * 1000;

      // Initial Load of Feature Toggles and updating them in a defined interval to ensure the correct values
      timer(0, intervalMs)
        .pipe(
          // temporary use hardcoded StaticContentService until ProfileService is adapted to return the new FeatureToggleResponse
          switchMap(() => store._staticContentService.getCommunityFeatureToggle()),
          takeUntilDestroyed(store._destroyRef),
        )
        .subscribe(_featureToggles => {
          patchState(store, { _featureToggles, togglesLoaded: true });
        });
    };
    return {
      isFeatureToggleEnabled,
      isFeatureToggleDisabled,
      isAnyFeatureToggleEnabled,
      areAllFeatureTogglesEnabled,
      areAllFeatureTogglesDisabled,
      _loadFeatureToggle,
    };
  }),
  withHooks(store => {
    return {
      onInit: () => store._loadFeatureToggle(),
    };
  }),
);
